import React, { useState, useEffect } from 'react';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripePromise, createSubscription, confirmSubscription, confirmSubscriptionInDatabase } from '../utils/stripeService';
import Button from './ui/Button';
import Icon from './AppIcon';
import FreeAccessNotice from './FreeAccessNotice';
import SiteMessageContainer from './SiteMessageContainer';

const CheckoutForm = ({ onSuccess, onError, isLoading, setIsLoading, hasHadTrial = false }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [clientSecret, setClientSecret] = useState('');
  const [subscriptionId, setSubscriptionId] = useState('');
  const [customerId, setCustomerId] = useState('');

  // Store hasHadTrial in a ref to avoid re-running effect when it changes
  const hasHadTrialRef = React.useRef(hasHadTrial);
  hasHadTrialRef.current = hasHadTrial;

  // Remove the useEffect that creates subscription on mount
  // Subscription will now be created only when user submits the form

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);

    const cardElement = elements.getElement(CardElement);

    try {
      // First, create payment method to validate the card
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (paymentMethodError) {
        throw new Error(paymentMethodError.message);
      }

      console.log('✅ Payment method created successfully');

      // Now create the subscription with the validated payment method
      console.log('🚀 Creating subscription after payment method validation...');
      const subscriptionResult = await createSubscription('premium', hasHadTrialRef.current);
      
      if (!subscriptionResult.success) {
        throw new Error(subscriptionResult.error || 'Failed to create subscription');
      }

      const { clientSecret: newClientSecret, subscriptionId: newSubscriptionId, customerId: newCustomerId } = subscriptionResult;

      // Handle different types of client secrets
      if (newClientSecret && newClientSecret !== 'trial_no_payment_required') {
        // Check if this is a setup intent (for trial subscriptions) or payment intent (for immediate charges)
        if (newClientSecret.startsWith('seti_')) {
          // This is a setup intent for trial subscription - confirm the setup intent to verify payment method
          console.log('🔍 Confirming setup intent for trial subscription...');
          const { error: setupError, setupIntent } = await stripe.confirmSetupIntent(newClientSecret, {
            payment_method: paymentMethod.id,
          });

          if (setupError) {
            console.error('❌ Setup intent confirmation failed:', setupError);
            onError(setupError.message || 'Payment method verification failed');
            return;
          }

          console.log('✅ Payment method verified for trial subscription');
          
          // Trial subscription with verified payment method - database record was already created
          onSuccess({
            subscriptionId: newSubscriptionId,
            customerId: newCustomerId,
            paymentIntent: null,
            setupIntent: setupIntent,
            trial: true
          });
        } else {
          // This is a payment intent for non-trial subscription - confirm payment first
          const paymentResult = await confirmSubscription(newClientSecret, paymentMethod.id);
          
          if (!paymentResult.success) {
            onError(paymentResult.error || 'Payment failed');
            return;
          }

          console.log('✅ Payment confirmed, now saving to database...');

          // Payment successful - now save subscription to database
          const dbResult = await confirmSubscriptionInDatabase(newSubscriptionId, paymentResult.paymentIntent?.id);
          
          if (dbResult.success) {
            onSuccess({
              subscriptionId: newSubscriptionId,
              customerId: newCustomerId,
              paymentIntent: paymentResult.paymentIntent
            });
          } else {
            // Payment succeeded but database save failed - this is a critical issue
            console.error('❌ Payment succeeded but database save failed:', dbResult.error);
            onError('Payment processed but subscription setup failed. Please contact support.');
          }
        }
      } else {
        // Fallback: Trial subscription without payment method verification
        // Database record was already created for trial subscriptions
        console.log('⚠️ Trial subscription without payment method verification');
        onSuccess({
          subscriptionId: newSubscriptionId,
          customerId: newCustomerId,
          paymentIntent: null,
          trial: true
        });
      }
    } catch (error) {
      console.error('❌ Subscription creation error:', error);
      onError(error.message || 'Payment failed');
    } finally {
      setIsLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#F8FAFC', // white text for dark theme
        backgroundColor: '#1E293B', // slate-800 background
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSmoothing: 'antialiased',
        '::placeholder': {
          color: '#94A3B8', // slate-400 for placeholder
        },
        ':-webkit-autofill': {
          color: '#F8FAFC',
        },
      },
      invalid: {
        color: '#DC2626', // red-600 for errors
        iconColor: '#DC2626',
      },
      complete: {
        color: '#10B981', // emerald-500 for valid input
        iconColor: '#10B981',
      },
    },
    hidePostalCode: false,
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Site Messages */}
      <SiteMessageContainer page="checkout" variant="compact" />

      {/* Free Access Notice */}
      <FreeAccessNotice variant="compact" />

      {/* Trial Information - only show if user hasn't had trial */}
      {!hasHadTrial && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Gift" size={20} className="text-green-600 dark:text-green-400" />
            <h3 className="font-semibold text-green-800 dark:text-green-200">7-Day Free Trial</h3>
          </div>
          <p className="text-sm text-green-700 dark:text-green-300">
            Your subscription starts with a 7-day free trial. You won't be charged until the trial ends.
            Cancel anytime during the trial period at no cost.
          </p>
        </div>
      )}

      {/* No trial notice for returning users */}
      {hasHadTrial && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Info" size={20} className="text-blue-600 dark:text-blue-400" />
            <h3 className="font-semibold text-blue-800 dark:text-blue-200">Welcome Back!</h3>
          </div>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            Your subscription will start immediately. You've already used your free trial.
          </p>
        </div>
      )}

      {/* Plan Summary */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="font-semibold text-foreground mb-3">Subscription Summary</h3>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Plan</span>
            <span className="font-medium">All Access Premium</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Price</span>
            <span className="font-medium">$5.00/month</span>
          </div>
          {!hasHadTrial && (
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Trial Period</span>
              <span className="font-medium text-green-600">7 days free</span>
            </div>
          )}
          <div className="border-t border-border pt-2 mt-2">
            <div className="flex justify-between items-center">
              <span className="font-semibold">Due Today</span>
              <span className={`font-semibold ${hasHadTrial ? 'text-foreground' : 'text-green-600'}`}>
                {hasHadTrial ? '$5.00' : '$0.00'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Method */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-foreground">
          Payment Method
        </label>
        <div className="p-3 border border-gray-600 rounded-lg bg-gray-800">
          <CardElement options={cardElementOptions} />
        </div>
        <p className="text-xs text-muted-foreground">
          {hasHadTrial 
            ? "Your payment method will be charged $5.00 today and then monthly."
            : "Your payment method will be charged $5.00 after your 7-day free trial ends."
          }
        </p>
      </div>

      {/* Security Notice */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon name="Shield" size={14} />
        <span>Your payment information is secure and encrypted</span>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={!stripe || isLoading}
        className="w-full"
        size="lg"
      >
        {isLoading ? (
          <>
            <Icon name="Loader2" size={16} className="animate-spin mr-2" />
            Processing...
          </>
        ) : (
          <>
            <Icon name="CreditCard" size={16} className="mr-2" />
            {hasHadTrial ? "Subscribe Now" : "Start Free Trial"}
          </>
        )}
      </Button>

      <p className="text-xs text-center text-muted-foreground">
        By clicking "{hasHadTrial ? "Subscribe Now" : "Start Free Trial"}", you agree to our Terms of Service and Privacy Policy.
        You can cancel your subscription at any time.
      </p>
    </form>
  );
};

const SubscriptionCheckout = ({ onSuccess, onError, onCancel, hasHadTrial = false }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSuccess = (result) => {
    setError('');
    onSuccess(result);
  };

  const handleError = (errorMessage) => {
    setError(errorMessage);
    onError(errorMessage);
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-card border border-border rounded-xl p-6 shadow-lg">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full mb-3">
            <Icon name="Crown" size={24} className="text-primary" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">Complete Your Subscription</h2>
          <p className="text-sm text-muted-foreground">
            {hasHadTrial 
              ? "Subscribe to continue accessing all games"
              : "Start your 7-day free trial to access all games"
            }
          </p>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={16} className="text-destructive" />
              <span className="text-sm text-destructive">{error}</span>
            </div>
          </div>
        )}

        <Elements stripe={stripePromise}>
          <CheckoutForm
            onSuccess={handleSuccess}
            onError={handleError}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            hasHadTrial={hasHadTrial}
          />
        </Elements>

        {onCancel && (
          <div className="mt-4 text-center">
            <button
              onClick={onCancel}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionCheckout;
